import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Receipt, AlertTriangle, Trash2, Plus, Download } from 'lucide-react';
import { toast } from 'sonner';
import { INVOICE_STATUS, INVOICE_STATUS_ORDER, INVOICE_TYPE, INVOICE_TYPE_ORDER, GBP } from './constants';
import { useAuth } from '../../contexts/AuthContext';
import SortHeader from '../../components/SortHeader';
import BulkDeleteDialog from '../../components/BulkDeleteDialog';
import { useTableTools, useSortedItems } from '../../components/tableTools';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';

const SORT_ACCESSORS = {
    invoice_number: i => i.invoice_number || '',
    client_name:    i => (i.client_name || '').toLowerCase(),
    type:           i => i.type || '',
    status:         i => i.status || '',
    issue_date:     i => i.issue_date || '',
    due_date:       i => i.due_date || '',
    total:          i => Number(i.total || 0),
    balance_due:    i => Number(i.balance_due || 0),
};

export default function InvoicesList() {
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState(sp.get('status') || 'all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [showOverdue, setShowOverdue] = useState(sp.get('overdue') === '1');
    const { selected, toggleOne, toggleAll, clear, sortKey, sortDir, changeSort } = useTableTools({ defaultSortKey: 'issue_date', defaultSortDir: 'desc' });
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [showExport, setShowExport] = useState(false);
    const sortedItems = useSortedItems(items, sortKey, sortDir, SORT_ACCESSORS);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (statusFilter !== 'all') params.status = statusFilter;
            if (typeFilter !== 'all') params.type = typeFilter;
            if (showOverdue) params.overdue = true;
            const r = await api.get('/invoices', { params });
            setItems(r.data.items);
            clear();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setLoading(false); }
    }, [search, statusFilter, typeFilter, showOverdue, clear]);

    useEffect(() => { load(); }, [load]);

    const openDeleteDialog = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        try {
            const r = await api.post('/invoices/bulk-delete-preview', { ids });
            setConfirmDelete({ counts: r.data.counts, names: r.data.sample_names, ids });
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
    };

    const doDelete = async (ids) => {
        try {
            const r = await api.post('/invoices/bulk-delete', { ids });
            const d = r.data.deleted || {};
            toast.success(`Deleted ${d.invoices || 0} invoice(s) + ${d.payments || 0} linked payment(s)`);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Delete failed'); }
    };

    return (
        <div data-testid="invoices-list-page" className="bm-fade-in">
            <PageHeader
                title="Invoices / Ledger"
                description="All invoices across clients. Overdue is auto-flagged when due_date has passed."
                actions={
                    <div className="flex gap-2 flex-wrap">
                        {isAdmin && selected.size > 0 && (
                            <Button onClick={openDeleteDialog} variant="outline" className="h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid="invoices-bulk-delete-btn">
                                <Trash2 size={15} className="mr-1.5" /> Delete {selected.size} selected
                            </Button>
                        )}
                        <Button onClick={() => setShowExport(true)} variant="outline" className="h-9" data-testid="invoices-export-crunch-btn">
                            <Download size={15} className="mr-1.5" /> Export to Crunch
                        </Button>
                        <Button onClick={() => setShowNew(true)} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="invoices-new-btn">
                            <Plus size={16} className="mr-1" /> New invoice
                        </Button>
                    </div>
                }
            >
                <div className="mt-5 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[160px] sm:min-w-[240px] max-w-sm">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--bm-text-subtle)]" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by number, client…" className="pl-9 h-9 bg-white w-full" data-testid="invoices-search-input" />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-9 w-[150px] bg-white" data-testid="invoices-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {INVOICE_STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{INVOICE_STATUS[s].label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                        <SelectTrigger className="h-9 w-[150px] bg-white" data-testid="invoices-type-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            {INVOICE_TYPE_ORDER.map(t => <SelectItem key={t} value={t}>{INVOICE_TYPE[t].label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <button
                        onClick={() => setShowOverdue(v => !v)}
                        className={`h-9 px-3 rounded-md text-[12.5px] font-medium border ${showOverdue ? 'bg-red-50 text-red-700 border-red-200' : 'bg-white text-[color:var(--bm-text-muted)] border-[color:var(--bm-border)]'}`}
                        data-testid="invoices-overdue-toggle"
                    >
                        <AlertTriangle size={14} className="inline mr-1" /> Overdue only
                    </button>
                </div>
            </PageHeader>

            <div className="px-4 sm:px-8 py-4 sm:py-6">
                {/* Mobile — card list (table below is for md+ only) */}
                <div className="md:hidden space-y-2.5" data-testid="invoices-mobile-list">
                    {loading && <div className="text-center text-[color:var(--bm-text-muted)] py-10">Loading…</div>}
                    {!loading && sortedItems.length === 0 && (
                        <div className="text-center py-14">
                            <Receipt size={32} className="mx-auto text-[color:var(--bm-text-subtle)]" />
                            <div className="mt-3 text-[color:var(--bm-text-muted)] text-[14px]">No invoices yet.</div>
                        </div>
                    )}
                    {!loading && sortedItems.map(i => (
                        <div key={i.id} onClick={() => navigate(`/invoices/${i.id}`)}
                             className="bg-white rounded-xl border border-[color:var(--bm-border)] p-4 active:bg-[color:var(--bm-bg-mute)]"
                             data-testid={`invoice-mobile-card-${i.id}`}>
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-semibold text-[color:var(--bm-text)] text-[14px]">{i.invoice_number}</span>
                                <span className={`bm-pill ${INVOICE_STATUS[i.status]?.tone || ''} border shrink-0`}>{INVOICE_STATUS[i.status]?.label || i.status}</span>
                            </div>
                            <div className="text-[13px] text-[color:var(--bm-text-muted)] mt-1 truncate">{i.client_name} · {INVOICE_TYPE[i.type]?.label || i.type}</div>
                            <div className="flex items-center justify-between mt-2.5 text-[12.5px] text-[color:var(--bm-text-subtle)]">
                                <span>Due {i.due_date}</span>
                                <div className="text-right">
                                    <div className="font-medium text-[color:var(--bm-text)]">{GBP(i.total)}</div>
                                    {Number(i.balance_due) > 0 && <div className="text-[11.5px]">Balance {GBP(i.balance_due)}</div>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Desktop / tablet — sortable table */}
                <div className="hidden md:block bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                    <table className="w-full text-[13px]">
                        <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                            <tr>
                                {isAdmin && (
                                    <th className="w-10 px-4 py-3">
                                        <input type="checkbox" checked={items.length > 0 && selected.size === items.length}
                                               onChange={() => toggleAll(items)} className="cursor-pointer"
                                               data-testid="invoices-select-all" />
                                    </th>
                                )}
                                <SortHeader label="Invoice"  sortKey="invoice_number" currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                <SortHeader label="Client"   sortKey="client_name"    currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                <SortHeader label="Type"     sortKey="type"           currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                <SortHeader label="Status"   sortKey="status"         currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                <SortHeader label="Issue"    sortKey="issue_date"     currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                <SortHeader label="Due"      sortKey="due_date"       currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                <SortHeader label="Total"    sortKey="total"          currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                                <SortHeader label="Balance"  sortKey="balance_due"    currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={isAdmin ? 9 : 8} className="px-5 py-10 text-center text-[color:var(--bm-text-muted)]">Loading…</td></tr>}
                            {!loading && sortedItems.length === 0 && (
                                <tr><td colSpan={isAdmin ? 9 : 8} className="px-5 py-14 text-center">
                                    <Receipt size={36} className="mx-auto text-[color:var(--bm-text-subtle)]" />
                                    <div className="mt-3 text-[color:var(--bm-text-muted)] text-[14px]">No invoices yet.</div>
                                    <div className="text-[12.5px] text-[color:var(--bm-text-subtle)] mt-1">Create invoices from a contract on the Contract detail page.</div>
                                </td></tr>
                            )}
                            {sortedItems.map(i => {
                                const isSel = selected.has(i.id);
                                return (
                                    <tr key={i.id} onClick={() => navigate(`/invoices/${i.id}`)}
                                        className={`border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] cursor-pointer ${isSel ? 'bg-[color:var(--bm-accent-50)]/60' : ''}`}
                                        data-testid={`invoice-row-${i.id}`}>
                                        {isAdmin && (
                                            <td className="px-4 py-3.5" onClick={(e) => { e.stopPropagation(); toggleOne(i.id); }}>
                                                <input type="checkbox" checked={isSel} onChange={() => toggleOne(i.id)}
                                                       onClick={(e) => e.stopPropagation()} className="cursor-pointer"
                                                       data-testid={`invoice-select-${i.id}`} />
                                            </td>
                                        )}
                                        <td className="px-5 py-3.5 font-medium text-[color:var(--bm-text)]">{i.invoice_number}</td>
                                        <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{i.client_name}</td>
                                        <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{INVOICE_TYPE[i.type]?.label || i.type}</td>
                                        <td className="px-4 py-3.5">
                                            <span className={`bm-pill ${INVOICE_STATUS[i.status]?.tone || ''} border`}>{INVOICE_STATUS[i.status]?.label || i.status}</span>
                                        </td>
                                        <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{i.issue_date}</td>
                                        <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{i.due_date}</td>
                                        <td className="px-4 py-3.5 text-right font-medium">{GBP(i.total)}</td>
                                        <td className="px-4 py-3.5 text-right font-semibold">{GBP(i.balance_due)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <BulkDeleteDialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} info={confirmDelete}
                              entityLabel="invoice" onConfirm={doDelete} testId="invoices-bulk-delete-dialog" />

            <NewInvoiceDialog open={showNew} onClose={() => setShowNew(false)} onCreated={(inv) => navigate(`/invoices/${inv.id}`)} />
            <ExportCrunchDialog open={showExport} onClose={() => setShowExport(false)} />
        </div>
    );
}

const EXPORT_STATUS_DEFAULTS = ['sent', 'part_paid', 'paid', 'overdue'];

function ExportCrunchDialog({ open, onClose }) {
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [statuses, setStatuses] = useState(EXPORT_STATUS_DEFAULTS);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        if (!open) return;
        const today = new Date();
        const jan1 = `${today.getFullYear()}-01-01`;
        setFrom(jan1);
        setTo(today.toISOString().slice(0, 10));
        setStatuses(EXPORT_STATUS_DEFAULTS);
    }, [open]);

    const toggleStatus = (s) =>
        setStatuses(v => v.includes(s) ? v.filter(x => x !== s) : [...v, s]);

    const doExport = async () => {
        if (from && to && from > to) return toast.error('“From” date must be on or before “To” date');
        if (statuses.length === 0) return toast.error('Select at least one status to export');
        setExporting(true);
        try {
            const params = { status: statuses.join(',') };
            if (from) params.date_from = from;
            if (to) params.date_to = to;
            const r = await api.get('/invoices/export/crunch', { params, responseType: 'blob' });
            const blob = new Blob([r.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `crunch-invoices-${from || 'all'}-to-${to || 'all'}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Crunch CSV downloaded');
            onClose();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Export failed');
        } finally { setExporting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-[460px]" data-testid="export-crunch-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-[20px]">Export to Crunch</DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">
                        Downloads a Crunch-import CSV — one row per invoice — for the chosen issue-date range and statuses.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[12.5px] font-medium">From (issue date)</Label>
                            <Input type="date" value={from} onChange={e => setFrom(e.target.value)}
                                   className="mt-1.5 h-10" data-testid="export-crunch-from" />
                        </div>
                        <div>
                            <Label className="text-[12.5px] font-medium">To (issue date)</Label>
                            <Input type="date" value={to} onChange={e => setTo(e.target.value)}
                                   className="mt-1.5 h-10" data-testid="export-crunch-to" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[12.5px] font-medium">Statuses to include</Label>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {INVOICE_STATUS_ORDER.map(s => {
                                const on = statuses.includes(s);
                                return (
                                    <button key={s} type="button" onClick={() => toggleStatus(s)}
                                            className={`h-8 px-3 rounded-full text-[12px] font-medium border transition-colors ${on ? 'bg-[color:var(--bm-primary)] text-white border-[color:var(--bm-primary)]' : 'bg-white text-[color:var(--bm-text-muted)] border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)]'}`}
                                            data-testid={`export-crunch-status-${s}`}>
                                        {INVOICE_STATUS[s].label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <DialogFooter className="pt-3">
                    <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                    <Button type="button" disabled={exporting} onClick={doExport}
                            style={{ background: 'var(--bm-primary)' }} data-testid="export-crunch-download-btn">
                        <Download size={15} className="mr-1.5" /> {exporting ? 'Exporting…' : 'Download CSV'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function NewInvoiceDialog({ open, onClose, onCreated }) {
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState('');
    const [type, setType] = useState('manual');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get('/clients', { params: { per_page: 200 } }).then(r => setClients(r.data.items)).catch(() => {});
        setClientId(''); setType('manual'); setNotes('');
    }, [open]);

    const submit = async (e) => {
        e.preventDefault();
        if (!clientId) return toast.error('Please select a client');
        setSubmitting(true);
        try {
            const r = await api.post('/invoices', {
                client_id: clientId,
                type,
                notes: notes.trim() || null,
                line_items: [],
            });
            toast.success(`Invoice ${r.data.invoice_number} created`);
            onCreated(r.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-[460px]" data-testid="new-invoice-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-[20px]">New invoice</DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">
                        Creates a blank draft invoice. Add line items on the next screen.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div>
                        <Label className="text-[12.5px] font-medium">Client *</Label>
                        <Select value={clientId} onValueChange={setClientId}>
                            <SelectTrigger className="mt-1.5 h-10" data-testid="new-invoice-client-select">
                                <SelectValue placeholder="Select client" />
                            </SelectTrigger>
                            <SelectContent>
                                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[12.5px] font-medium">Invoice type</Label>
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger className="mt-1.5 h-10" data-testid="new-invoice-type-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {INVOICE_TYPE_ORDER.map(t => <SelectItem key={t} value={t}>{INVOICE_TYPE[t].label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[12.5px] font-medium">Notes <span className="text-[color:var(--bm-text-subtle)] font-normal">(optional)</span></Label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)}
                                  placeholder="e.g. Q1 SaaS subscription, March 2026…"
                                  rows={2}
                                  className="mt-1.5 w-full rounded-md border border-[color:var(--bm-border)] p-3 text-[13px] focus:outline-none focus:ring-1 focus:ring-[color:var(--bm-primary)]" />
                    </div>
                    <DialogFooter className="pt-2">
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={submitting} style={{ background: 'var(--bm-primary)' }} data-testid="new-invoice-submit-btn">
                            {submitting ? 'Creating…' : 'Create invoice'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
